import { useCallback, useEffect, useState } from "react";
import './OrderView.scss'
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import MobileLoader from "../../components/MobileLoader/MobileLoader";
import { getEncryptedSessionData, setEncryptedSessionData } from "../../Services/SessionStorageService";

interface PropData {
    isShow: boolean
    isClose: (e?: any, res?: any, menuId?: number | string) => void;
    data: { loungeId: number; tableNo: string; } | null
};

type MenuItem = {
    id: string;
    name: string;
    gross_price: number;
    server_category_id?: string;
    category_id?: string;
    quantity?: number;
};

type MenuDataCache = {
    categories: { id: string; name: string }[];
    menus: MenuItem[];
};


const getCategoryOrder = (name: string): number => {
    const cleanName = name.toLowerCase().trim();

    if (cleanName === "soup" || cleanName === "soups") return 0;
    if (cleanName === "munchies" || cleanName === "munchie") return 1;
    if (cleanName === "salad" || cleanName === "salads") return 2;
    if (cleanName === "veg starter" || cleanName === "veg starters") return 3;
    if (cleanName === "paneer starter" || cleanName === "paneer starters") return 4;
    if (cleanName === "rolls" || cleanName === "roll") return 5;
    if (cleanName === "kebab" || cleanName === "kebabs") return 6;
    if (cleanName === "tikka" || cleanName === "tikkas") return 7;
    if (cleanName === "noodles" || cleanName === "noodle") return 8;
    if (cleanName === "veg course" || cleanName === "veg courses") return 9;
    if (cleanName === "paneer course" || cleanName === "paneer courses") return 10;
    if (cleanName === "kofta" || cleanName === "koftas") return 11;
    if (cleanName === "curry" || cleanName === "curries") return 12;
    if (cleanName === "dal" || cleanName === "dals" || cleanName === "daal") return 13;
    if (cleanName === "rice" || cleanName === "rices") return 14;
    if (cleanName === "curd/raita" || cleanName === "curd" || cleanName === "raita" || cleanName === "curd / raita" || cleanName === "curd/ raita") return 15;
    if (cleanName === "indian breads" || cleanName === "indian bread" || cleanName === "breads" || cleanName === "bread") return 16;
    if (cleanName === "platter" || cleanName === "platters") return 17;
    if (cleanName === "mocktails" || cleanName === "mocktail") return 18;
    if (cleanName === "shakes" || cleanName === "shake") return 19;

    if (cleanName.includes("soup")) return 0;
    if (cleanName.includes("munch")) return 1;
    if (cleanName.includes("salad")) return 2;
    if (cleanName.includes("veg starter")) return 3;
    if (cleanName.includes("paneer starter")) return 4;
    if (cleanName.includes("roll")) return 5;
    if (cleanName.includes("kebab")) return 6;
    if (cleanName.includes("tikka")) return 7;
    if (cleanName.includes("noodle")) return 8;
    if (cleanName.includes("veg course")) return 9;
    if (cleanName.includes("paneer course")) return 10;
    if (cleanName.includes("kofta")) return 11;
    if (cleanName.includes("curry") || cleanName.includes("curries")) return 12;
    if (cleanName.includes("dal") || cleanName.includes("daal")) return 13;
    if (cleanName.includes("rice")) return 14;
    if (cleanName.includes("curd") || cleanName.includes("raita")) return 15;
    if (cleanName.includes("bread")) return 16;
    if (cleanName.includes("platter")) return 17;
    if (cleanName.includes("mocktail")) return 18;
    if (cleanName.includes("shake")) return 19;

    return 1000;
};

const AddItemModal = (props: PropData) => {
    const [selected, setSelected] = useState<MenuItem[]>([])
    const [loading, setLoading] = useState(false)
    const [activeTab, setActiveTab] = useState<string>("all")
    const [search, setSearch] = useState("")
    const [categoryListOptions, setCategoryListOptions] = useState<{ id: string; name: string }[]>([])
    const [allMenuListOptions, setAllMenuListOptions] = useState<MenuItem[]>([])
    const [menuListOptions, setMenuListOptions] = useState<MenuItem[]>([])
    const tenantId = localStorage.getItem("tenantId") || "default";
    const menuDataCacheKey = `menu_data_${tenantId}`;
    const applyMenuData = useCallback((data: MenuDataCache) => {
        const sortedCats = [...(data.categories || [])].sort((a, b) => {
            const orderA = getCategoryOrder(a.name);
            const orderB = getCategoryOrder(b.name);
            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name);
        });
        setCategoryListOptions(sortedCats);
        setAllMenuListOptions(data.menus);
        setMenuListOptions(data.menus);
    }, [])

    const getMenuData = useCallback(() => {
        const cachedMenuData = getEncryptedSessionData<MenuDataCache>(menuDataCacheKey);
        if (cachedMenuData) {
            applyMenuData(cachedMenuData);
        } else {
            setLoading(true)
        }

        Promise.all([
            WebService.getAPI({ action: '/get-all/menu-categories' }),
            WebService.getAPI({
                action: '/get-all/menu-items',
                body: {}
            })
        ])
            .then(([categoryResponse, menuResponse]: any[]) => {
                const menuData = {
                    categories: categoryResponse?.categories?.length > 0 ? categoryResponse.categories : [],
                    menus: menuResponse?.menus?.length > 0 ? menuResponse.menus : []
                };
                applyMenuData(menuData);
                setEncryptedSessionData(menuDataCacheKey, menuData);
            })
            .catch((err) => console.error("Menu data swr fetch error:", err))
            .finally(() => setLoading(false))
    }, [applyMenuData, menuDataCacheKey])

    useEffect(() => {
        if (props.isShow) {
            setActiveTab("all");
            setSearch("");
            getMenuData()
        }
    }, [props.isShow, getMenuData])

    if (!props.isShow) return null

    const toggleSelect = (item: MenuItem) => {
        setSelected(prev => {
            const exists = prev.find(i => i.id === item.id)
            if (exists) {
                return prev.filter(i => i.id !== item.id)
            } else {
                return [...prev, { ...item, quantity: 1 }]
            }
        })
    }

    const isSelected = (id: string) => {
        return selected.some(item => item.id === id)
    }

    const handleTabClick = (tab: string, category_id?: string) => {
        setActiveTab(tab)
        if (tab === "all") {
            setMenuListOptions(allMenuListOptions)
        } else {
            const filteredMenus = allMenuListOptions.filter((item) =>
                String(item.server_category_id || item.category_id) === String(category_id)
            );
            setMenuListOptions(filteredMenus)
        }
    }

    const handleAdd = () => {
        if (selected.length === 0) {
            toast.error("Please select at least one item");
            return;
        }
        props.isClose(true, selected);
        setSelected([]);
    }

    const handleClose = () => {
        setSelected([])
        props.isClose()
    }

    const filteredMenu = menuListOptions.filter((item: any) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        const codeValue = item.custom_code ?? item.code ?? item.item_code ?? "";
        if (/^\d+$/.test(q)) {
            return String(codeValue).trim().toLowerCase() === q;
        }
        const nameMatch = item.name.toLowerCase().includes(q);
        const codeMatch = String(codeValue).trim().toLowerCase() === q;
        return nameMatch || codeMatch;
    });

    return (
        <>
            <div className="modal-overlay">
                <div className="full-modal">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Menu Item</h5>
                        <button className="close-btn" onClick={handleClose}>✕</button>
                    </div>

                    <div className="menu-search">
                        <input
                            type="text"
                            placeholder="Search menu items"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="tab-container">
                        <div className={`tab-item ${activeTab === "all" ? "active" : ""}`} onClick={() => handleTabClick("all")}>
                            All
                        </div>

                        {categoryListOptions.map((cat) => (
                            <div key={cat.id} className={`tab-item ${activeTab === cat.id ? "active" : ""}`} onClick={() => handleTabClick(cat.id, cat.id)}>
                                {cat.name}
                            </div>
                        ))}
                    </div>

                    <div className="modal-body">
                        {loading ? (
                            <div>
                                <MobileLoader show={loading} />
                            </div>
                        ) : filteredMenu.length === 0 ? (<div className="no-menu">No menu Items Available</div>)
                            : (
                                filteredMenu.map((item: any) => (
                                    <div key={item.id} className={`menu-item ${isSelected(item.id) ? "active" : ""}`} onClick={() => toggleSelect({ id: item.id, name: item.name, gross_price: item.gross_price, quantity: 1 })} >
                                        <div className="menu-left">{item.name}</div>
                                        <div className="menu-right">
                                            ₹{item.gross_price}
                                        </div>
                                    </div>
                                ))
                            )}
                    </div>
                    <div className="modal-footer">
                        <button className="add-selected-btn" onClick={handleAdd}>
                            Add Selected ({selected.length})
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AddItemModal;
